<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<app-tab-item :tab="tab" value="tags">
					<app-heading :title="`${website.name} Tags`">
						<template v-slot:left>
							<app-button v-if="$ravello.can('website.post')"
								class="d-none d-sm-inline-flex"
								color="success"
								outlined
								small
								:to="{ name: 'website-tag', params: { tag: 'new' } }"
							>Add Tag</app-button>
						</template>
						<template v-slot:right>
							<app-tooltip text="Generate tags from Studeo data">
								<app-button v-if="$ravello.can('website.post')"
									color="secondary"
									icon="fa-magic"
									small
									outlined
									@click="openGenerateDialog"
								>From Studeo</app-button>
							</app-tooltip>
							<app-tooltip text="Automatically link complexes and units using Studeo tags">
								<app-button v-if="$ravello.can('website.post')"
									color="secondary"
									icon="fa-sync"
									small
									outlined
									@click="onAutoLink"
								>Auto-link</app-button>
							</app-tooltip>
						</template>
					</app-heading>
					<app-row>
						<app-cell>
							<app-data-table
								:items="tags"
								:headers="headers"
								:total="count"
								:refresh.sync="refresh"
								@update="fetchTags"
							>
								<template v-slot:actions="{ item }">
									<app-button-confirm
										v-if="$ravello.can('website.delete')"
										color="error"
										icon="fa-trash"
										table
										label-confirm="Remove"
										:description="`Are you sure you want to remove tag <b>${item.name}</b>?`"
										title="Remove Tag"
										@click="onDelete(item._id)"
									>
										Remove
									</app-button-confirm>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
					<app-floating-button
						class="d-inline-flex d-sm-none"
						:to="{ name: 'website-tag', params: { tag: 'new' } }"
						color="success"
						icon="fa-plus"
					/>
				</app-tab-item>
			</template>
		</app-tabs>
		<!-- Generate from Studeo dialog box -->
		<app-dialog
			v-model="generate.open"
			max-width="640"
			title="Generate From Studeo"
			persistent
		>
			<app-dialog-body>
				<app-row>
					<app-cell>
						<input-select
							v-model="generate.value"
							:items="generate.types"
							label="Data type"
						/>
					</app-cell>
				</app-row>
				<app-row v-if="isResortDataType">
					<app-cell>
						<input-select
							v-model="generate.resorts"
							:items="generate.websiteResorts"
							label="Resorts"
							multiple
						/>
					</app-cell>
				</app-row>
				<app-row v-if="generate.value === 'amenity'">
					<app-cell>
						<input-combobox
							v-model="generate.amenities"
							:items="generate.websiteAmenities"
							label="Amenities"
							multiple
						/>
					</app-cell>
				</app-row>
				<app-row
					v-if="generate.value === 'amenity' && amenitiesWithEnumeratedMeta.length"
				>
					<app-cell>
						<div class="caption text--secondary mb-2">
							Optional: restrict autolink to units whose Studeo amenity details match (e.g. private hot tub).
						</div>
						<div
							v-for="amen in amenitiesWithEnumeratedMeta"
							:key="amen._id"
							class="mb-4"
						>
							<div class="subtitle-2">{{ amen.label }}</div>
							<app-row
								v-for="fieldKey in metadataEnumeratedKeys(amen.metadata)"
								:key="`${amen._id}-${fieldKey}`"
								no-padding
							>
								<app-cell cols="12">
									<input-select
										v-model="generate.amenityMatchDetails[amen._id][fieldKey]"
										:items="metadataEnumItems(amen.metadata[fieldKey])"
										:label="enumFieldLabel(amen.metadata[fieldKey], fieldKey)"
										clearable
									/>
								</app-cell>
							</app-row>
						</div>
					</app-cell>
				</app-row>
				<app-row>
					<app-cell>
						<input-switch
							v-model="generate.autolink"
							label="Auto-link complexes and units to generated tags?"
						/>
					</app-cell>
				</app-row>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					color="secondary"
					flat="flat"
					@click="onGenerate"
				>Generate</app-button>
				<app-button
					flat="flat"
					@click="generate.open = false"
				>Cancel</app-button>
			</app-dialog-footer>
		</app-dialog>
	</app-page>
</template>

<script>
import { websiteContentTabs } from '@/datasources/tabs'

export default {
	data() {
		return {
			website: {
				name: '',
				enterprise: {
					_id: null,
				},
			},
			tags: [],
			count: -1,
			refresh: false,
			tabs: [],
			generate: {
				open: false,
				types: [{
					text: 'Property types',
					value: 'property_type',
				}, {
					text: 'Areas',
					value: 'area',
				}, {
					text: 'Locations',
					value: 'location',
				}, {
					text: 'Amenities',
					value: 'amenity',
				}, {
					text: 'Pets Friendly',
					value: 'pet',
				}],
				value: null,
				enterpriseTypes: { property_type: true, amenity: true, pet: true },
				websiteResorts: [],
				websiteAmenities: [],
				resorts: [],
				amenities: [],
				amenityCatalog: [],
				amenityMatchDetails: {},
				autolink: false,
				init: false,
			},
			headers: [{
				text: 'Name',
				value: 'name',
				sortable: true,
			}, {
				text: 'Slug',
				value: 'slug',
				sortable: true,
			}, {
				text: 'Generated from Studeo',
				value: 'generated',
				sortable: true,
				cb: item => (item.generated ? 'Yes' : 'No'),
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}],
		}
	},
	computed: {
		websiteId() {
			return this.$route.params.website
		},
		isResortDataType() {
			return this.generate.value && !this.generate.enterpriseTypes[this.generate.value]
		},
		selectedAmenitiesWithMeta() {
			const ids = this.normalizeAmenitySelection(this.generate.amenities)
			const catalog = this.generate.amenityCatalog || []
			return ids.map((id) => {
				const row = catalog.find(a => String(a._id) === String(id))
				if (!row) {
					return null
				}
				const label = this.getAmenityTranslation(row.translations, 'en')
					|| (row.studeo_id != null ? `Amenity ${row.studeo_id}` : String(row._id))
				return {
					_id: String(row._id),
					label,
					metadata: row.metadata,
				}
			}).filter(Boolean)
		},
		amenitiesWithEnumeratedMeta() {
			return this.selectedAmenitiesWithMeta.filter(a => this.metadataEnumeratedKeys(a.metadata).length > 0)
		},
	},
	watch: {
		'generate.amenities': {
			handler() {
				this.syncAmenityMatchShells()
			},
			deep: true,
		},
	},
	async mounted() {
		this.website = await this.$ravello.graphql('website', this.websiteId)
			.fields(['name'])
			.query()
		this.tabs = websiteContentTabs(this.websiteId)
	},
	methods: {
		getAmenityTranslation(translations, lang = 'en') {
			if (!translations) {
				return ''
			}
			if (typeof translations === 'string') {
				return translations
			}
			if (translations[lang]) {
				return translations[lang]
			}
			const keys = Object.keys(translations)
			return keys.length > 0 ? translations[keys[0]] : ''
		},

		normalizeAmenitySelection(selection) {
			if (!selection || !selection.length) {
				return []
			}
			return selection.map((item) => {
				if (item == null) {
					return null
				}
				if (typeof item === 'object' && item.value !== undefined) {
					return item.value
				}
				return item
			}).filter(id => id != null)
		},

		metadataEnumeratedKeys(metadata) {
			if (!metadata || typeof metadata !== 'object') {
				return []
			}
			return Object.keys(metadata).filter((fieldKey) => {
				const f = metadata[fieldKey]
				return f && Array.isArray(f.enumerations) && f.enumerations.length
			})
		},

		metadataEnumItems(fieldMeta) {
			return (fieldMeta.enumerations || []).map(e => ({
				text: this.getAmenityTranslation(e.name, 'en') || e.value,
				value: e.value,
			}))
		},

		enumFieldLabel(fieldMeta, fieldKey) {
			return this.getAmenityTranslation(fieldMeta.name, 'en') || fieldKey
		},

		cleanAmenityMatchObject(obj) {
			if (!obj || typeof obj !== 'object') {
				return null
			}
			const out = {}
			Object.keys(obj).forEach((key) => {
				const val = obj[key]
				if (val === null || val === undefined || val === '') {
					return
				}
				out[key] = val
			})
			return Object.keys(out).length ? out : null
		},

		buildAmenitiesGeneratePayload() {
			const ids = this.normalizeAmenitySelection(this.generate.amenities)
			return ids.map((id) => {
				const k = String(id)
				const cleaned = this.cleanAmenityMatchObject(this.generate.amenityMatchDetails[k])
				return cleaned && Object.keys(cleaned).length
					? { _id: id, match_details: cleaned }
					: id
			})
		},

		syncAmenityMatchShells() {
			const ids = this.normalizeAmenitySelection(this.generate.amenities || [])
			ids.forEach((id) => {
				const k = String(id)
				const row = (this.generate.amenityCatalog || []).find(a => String(a._id) === k)
				if (!row) {
					return
				}
				if (!this.generate.amenityMatchDetails[k]) {
					this.$set(this.generate.amenityMatchDetails, k, {})
				}
				const shell = this.generate.amenityMatchDetails[k]
				this.metadataEnumeratedKeys(row.metadata).forEach((fieldKey) => {
					if (!(fieldKey in shell)) {
						this.$set(shell, fieldKey, null)
					}
				})
			})
			Object.keys(this.generate.amenityMatchDetails).forEach((k) => {
				if (!ids.some(id => String(id) === k)) {
					this.$delete(this.generate.amenityMatchDetails, k)
				}
			})
		},

		async fetchTags(pagination) {
			const request = this.$ravello.graphql('tags')
				.parent(this.$ravello.graphql('website', this.websiteId))
				.fields(['_id', 'name', 'slug', 'generated'])
				.fromPagination(pagination)

			if (this.count === -1) {
				request.count('count')
			}
			try {
				this.tags = await request.query()
				if (this.count === -1) {
					this.count = request.results.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async onDelete(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('tags')
					.to(id)
					.delete()
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async openGenerateDialog() {
			if (!this.generate.init) {
				this.generate.websiteResorts = await this.$ravello.graphql('resorts')
					.parent(this.$ravello.graphql('enterprise')
						.parent(this.$ravello.graphql('website', this.websiteId)))
					.datasource()
					.query()
				const rawAmenities = await this.$ravello.graphql('amenities')
					.fields(['_id', 'translations', 'studeo_id', 'metadata'])
					.sort('studeo_id')
					.limit(10000)
					.query()
				this.generate.amenityCatalog = rawAmenities
				this.generate.websiteAmenities = rawAmenities.map((amenity) => {
					const text = this.getAmenityTranslation(amenity.translations, 'en')
						|| (amenity.studeo_id != null ? `Amenity ${amenity.studeo_id}` : String(amenity._id))
					return { text, value: amenity._id }
				})
				this.generate.init = true
			}

			this.generate.value = null
			this.generate.autolink = false
			this.generate.resorts = []
			this.generate.amenities = []
			this.generate.amenityMatchDetails = {}
			this.generate.open = true
		},

		async onGenerate() {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('tags')
					.to('generate')
					.create({
						type: this.generate.value,
						autolink: this.generate.autolink,
						...(this.isResortDataType ? { resorts: this.generate.resorts } : {}),
						...(this.generate.value === 'amenity'
							? { amenities: this.buildAmenitiesGeneratePayload() }
							: {}),
					})
				this.refresh = true
				this.generate.open = false
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async onAutoLink() {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('tags')
					.to('autolink')
					.create()

				this.$notifications.pushAlert('success', 'Studeo tags will update shortly!')
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},
	},
}
</script>
